import { useEffect } from 'react'
import type { Variable } from '../editor/types'
import { evaluateFormula, extractFormulaVariables } from './formula'

/**
 * Recalculates all formula variables whenever variableValues change.
 * Uses topological sort to handle formula chains (A depends on B depends on C).
 * Guards against infinite loops with a max-iteration cap.
 */
export function useFormulaEvaluation(
  variables: Variable[],
  variableValues: Record<string, number | string | boolean>,
  setVariableValue: (name: string, value: number | string | boolean) => void
) {
  useEffect(() => {
    const formulaVars = variables.filter((v) => v.type === 'formula' && v.formula)
    if (formulaVars.length === 0) return

    // Topological sort: evaluate formulas in dependency order
    const sorted = topoSort(formulaVars)

    // Work on a snapshot so we can resolve chains
    const snapshot = { ...variableValues }
    let changed = false

    for (const v of sorted) {
      const result = Math.round(evaluateFormula(v.formula!, snapshot) * 1e6) / 1e6
      if (snapshot[v.name] !== result) {
        snapshot[v.name] = result
        changed = true
      }
    }

    if (changed) {
      for (const v of sorted) {
        const result = snapshot[v.name]
        if (variableValues[v.name] !== result) {
          setVariableValue(v.name, result as number)
        }
      }
    }
  }, [variables, variableValues, setVariableValue])
}

function topoSort(formulaVars: Variable[]): Variable[] {
  const nameSet = new Set(formulaVars.map((v) => v.name))
  const deps = new Map<string, string[]>()
  for (const v of formulaVars) {
    const refs = extractFormulaVariables(v.formula!).filter((n) => nameSet.has(n))
    deps.set(v.name, refs)
  }

  const visited = new Set<string>()
  const result: Variable[] = []
  const visiting = new Set<string>()
  const varMap = new Map(formulaVars.map((v) => [v.name, v]))

  function visit(name: string) {
    if (visited.has(name)) return
    if (visiting.has(name)) return // cycle — skip
    visiting.add(name)
    for (const dep of deps.get(name) || []) {
      visit(dep)
    }
    visiting.delete(name)
    visited.add(name)
    const v = varMap.get(name)
    if (v) result.push(v)
  }

  for (const v of formulaVars) {
    visit(v.name)
  }

  return result
}
